import React from "react";
import { fetchQuery, graphql } from "react-relay";
import {
	components,
	type GroupBase,
	type MultiValueGenericProps,
	type OnChangeValue,
	type OptionProps,
	type OptionsOrGroups,
	type SingleValueProps,
} from "react-select";
import Select from "react-select/async";

import { faBook, faCalendarRange } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faUserFriends, faUsers } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { compact, debounce, uniqBy } from "lodash";
import moment from "moment";

import { getSavedData, type StreamVisitedItem } from "~/lib/searchHistory";
import { Twix } from "~/lib/twix";
import { displayNoteType } from "~/lib/utils";
import { environment } from "~/models";
import reactSelect, { MultiValueRemove } from "~/ui/react-select";

import type { StreamSelectEvent$data } from "~/__generated__/StreamSelectEvent.graphql";
import type { StreamSelectLoadAllJumpToQuery } from "~/__generated__/StreamSelectLoadAllJumpToQuery.graphql";
import type { StreamSelectStream$data } from "~/__generated__/StreamSelectStream.graphql";

import styles from "./css/StreamSelect.module.css";

export interface StreamOrEventType {
	id: string;
	url: string;
	title: string;
	start: string | null;
	end: string | null;
	type: string;
	otherUserId: string | null;
	latestNoteId: string | null;
	isShared: boolean;
}

type StreamOrEventOptionType = {
	label: string;
	value: string;
	item: StreamOrEventType;
};

export const getTypeIcon = (type: string, shared?: boolean) => {
	return (
		<FontAwesomeIcon
			icon={shared ? faUsers : type === "meeting" ? faCalendarRange : type === "oneonone" ? faUserFriends : faBook}
		/>
	);
};

function SingleValue(props: SingleValueProps<StreamOrEventOptionType>) {
	const { item } = props.data;
	const RSSingleValue = components.SingleValue;
	return (
		<RSSingleValue {...props} className={cx(props.selectProps.className, styles.singleValue)}>
			{getTypeIcon(item.type, item.isShared)}
			<div className={styles.labelText}>{item.title}</div>
		</RSSingleValue>
	);
}

function MultiValueLabel(props: MultiValueGenericProps<StreamOrEventOptionType>) {
	const { item } = props.data;
	const RSMultiValueLabel = components.MultiValueLabel;
	props.innerProps.className = cx(props.innerProps.className, styles.multiValueLabel);
	return (
		<RSMultiValueLabel {...props}>
			{getTypeIcon(item.type, item.isShared)}
			<div className={styles.labelText}>{item.title}</div>
		</RSMultiValueLabel>
	);
}

function Option(props: OptionProps<StreamOrEventOptionType, boolean>) {
	const { label, item } = props.data;
	const RSOption = components.Option;
	const latestNoteTimeRange = React.useMemo(() => {
		if (item.start && item.end) {
			return new Twix(moment(item.start), moment(item.end)).format({
				spaceBeforeMeridiem: false,
			});
		}
		return displayNoteType(item.type, item.isShared);
	}, [item]);

	return (
		<RSOption {...props} className={cx(props.className, styles.option)}>
			<div className={styles.optionName}>{label}</div>
			<div className={styles.optionMeta}>
				{getTypeIcon(item.type, item.isShared)} {latestNoteTimeRange}
			</div>
		</RSOption>
	);
}

graphql`
	fragment StreamSelectStream on NoteStream {
		id
		displayName
		type
		isShared
		url
		latestNote {
			id
			time
			eventStart
			eventEnd
			isLocked
			isHistoryPaywalled
		}
		otherUser {
			id
		}
	}
`;

function streamToItem(stream: Omit<StreamSelectStream$data, " $fragmentType"> | StreamVisitedItem): StreamOrEventType {
	return {
		id: stream.id,
		type: stream.type,
		title: stream.displayName || "Untitled note series",
		start: "latestNote" in stream ? (stream.latestNote?.eventStart ?? null) : null,
		end: "latestNote" in stream ? (stream.latestNote?.eventEnd ?? null) : null,
		url: stream.url || "",
		otherUserId: stream.otherUser?.id ?? null,
		latestNoteId: "latestNote" in stream ? (stream.latestNote?.id ?? null) : null,
		isShared: "isShared" in stream ? stream.isShared : false,
	};
}

graphql`
	fragment StreamSelectEvent on Event {
		id
		title
		eventUrl: url
		start
		end
		originalRecurringEventGid
		note {
			stream {
				id
				type
				isMerged
				...StreamSelectStream @relay(mask: false)
			}
		}
	}
`;

function eventToItem(event: Omit<StreamSelectEvent$data, " $fragmentType">): StreamOrEventType {
	return {
		id: event.id,
		type: "meeting",
		title: event.title || "Untitled event",
		start: event.start,
		end: event.end,
		url: event.eventUrl,
		otherUserId: null,
		latestNoteId: null,
		isShared: false,
	};
}

export async function searchForAll(query: string, allowMissingStreams: boolean) {
	let items = [];
	const results = await fetchQuery<StreamSelectLoadAllJumpToQuery>(
		environment,
		graphql`
			query StreamSelectLoadAllJumpToQuery($query: String!) {
				viewer {
					jumpToSearch(query: $query) {
						edges {
							node {
								__typename

								... on NoteStream {
									id
									type
									isMerged
									notesCount
									...StreamSelectStream @relay(mask: false)
								}

								... on Note {
									stream {
										id
										type
										isMerged
										...StreamSelectStream @relay(mask: false)
									}
								}

								... on Event {
									...StreamSelectEvent @relay(mask: false)
								}
							}
						}
					}
				}
			}
		`,
		{ query },
	).toPromise();
	items = compact(results?.viewer?.jumpToSearch?.edges.map(n => n?.node));

	let streams = [];
	let events = [];
	for (const node of items) {
		if (node.__typename === "Note") streams.push(node.stream);
		else if (node.__typename === "NoteStream" && node.notesCount > 0) streams.push(node);
		else if (node.__typename === "Event") {
			const noteStream = node.note?.stream;
			if (noteStream) streams.push(noteStream);
			else if (allowMissingStreams) events.push(node);
		}
	}
	streams = uniqBy(
		streams.filter(s => s.type !== "goalset" && !s.isMerged),
		s => s.id,
	);
	events = uniqBy(events, e => e.originalRecurringEventGid);

	return streams.map(s => streamToItem(s)).concat(events.map(e => eventToItem(e)));
}

const noOptionsMessage = ({ inputValue }: { inputValue: string }) => {
	if (!inputValue) return "Start typing to search for series or events";
	return "No series or event found for input";
};

interface StreamSelectProps {
	onChange: (selected: StreamOrEventType[]) => void;
	selection?: StreamOrEventType[];
	placeholder?: string;
	isMulti?: boolean;
	autoFocus?: boolean;
	allowMissingStreams?: boolean;
	isClearable?: boolean;
}

export default function StreamSelect({
	onChange,
	selection,
	placeholder,
	isMulti = false,
	autoFocus = false,
	allowMissingStreams = false,
	isClearable = false,
}: StreamSelectProps) {
	const getOptionsForSearch = React.useMemo(
		() =>
			debounce(
				(
					query: string,
					callback: (options: OptionsOrGroups<StreamOrEventOptionType, GroupBase<StreamOrEventOptionType>>) => void,
				) => {
					searchForAll(query, allowMissingStreams).then(items => {
						callback(
							compact(
								items.map(item => ({
									value: item.id,
									label: item.title,
									item,
								})),
							),
						);
					});
				},
				300,
				{ leading: false },
			),
		[allowMissingStreams],
	);

	const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLElement>) => {
		const target = event.nativeEvent.target;
		if (!target || !(target instanceof HTMLInputElement)) return;
		switch (event.key) {
			case "Home":
				target.setSelectionRange(0, 0);
				break;
			case "End": {
				const len = target.value.length;
				target.setSelectionRange(len, len);
				break;
			}
			default:
				return;
		}
		event.preventDefault();
		target.scrollIntoView();
	}, []);

	const onSelectChange = React.useCallback(
		(selection: OnChangeValue<StreamOrEventOptionType, boolean>) => {
			if (!selection) {
				return onChange([]);
			}
			if ("length" in selection) {
				return onChange(selection.map(({ item }) => item || null).filter(Boolean));
			}
			return onChange([selection.item]);
		},
		[onChange],
	);

	const defaultOptions = uniqBy(
		compact(
			getSavedData().visited.map(
				i =>
					i.version === 2 && {
						label: i.displayName,
						value: i.id,
						item: streamToItem(i),
					},
			),
		),
		o => o.value,
	);

	return (
		<Select<StreamOrEventOptionType, boolean>
			isMulti={isMulti}
			{...reactSelect}
			cacheOptions
			autoFocus={autoFocus}
			menuPlacement="auto"
			maxMenuHeight={260}
			components={{ Option, SingleValue, MultiValueLabel, MultiValueRemove }}
			isClearable={isClearable}
			defaultOptions={defaultOptions}
			loadOptions={getOptionsForSearch}
			placeholder={placeholder || undefined}
			noOptionsMessage={noOptionsMessage}
			onChange={onSelectChange}
			onKeyDown={handleKeyDown}
			{...(selection
				? {
						value: selection.map(s => ({
							label: s.title,
							value: s.id,
							item: s,
						})),
					}
				: {})}
		/>
	);
}
